import React, { useContext, useEffect, useRef } from 'react';
import _findIndex from 'lodash/findIndex';
import { useCombinedRefs } from 'common';
import classnames from 'classnames';
import MoreButton from 'components/MoreButton';
import KEY_CODES from 'common/KeyCodes';
import { CONSTANTS } from 'common/ListKeyboardNav';
import TREE_CONSTANTS from './constants';
import { TreeContext, TreeBranchContext, TreeNodeContext } from './';
import Styled from './styles';

const { TAB } = KEY_CODES;
const { LIST_ITEM_TABABLE_ITEM } = CONSTANTS;
const { TREE_NODE, LIST_CONTEXT_MENU_ITEM } = TREE_CONSTANTS;

export interface TreeMoreButtonProps extends React.ComponentPropsWithRef<'button'> {
  /** To render the menu items, consider using 'MoreButtonItem', 'MoreButtonItemDivider', 'MoreButtonItemGroupHeader'. */
  children?: React.ReactNode;
  /** Used for keyboard navigation to `TreeMoreButton`. The value must be the same value as the `id` of the tree item. */
  treeItemId?: string | number;
  /** If true, place focus on this item. */
  focusItem?: boolean;
  /** Override the `MoreButton` properties. See `MoreButton` component for details. */
  moreButtonProps?: {
    onKeyDown?: React.KeyboardEventHandler;
    onFocus?: React.FocusEventHandler;
    onBlur?: React.FocusEventHandler;
  };
}

/**
  Component to add context menu to a tree item. All other props will be passed to the root element.
 */
const TreeMoreButton = React.forwardRef<HTMLElement, TreeMoreButtonProps>((props: TreeMoreButtonProps, ref) => {
  const { treeItemId, focusItem, moreButtonProps, children, ...otherProps } = props;
  const focusRef = useRef(null);
  const combinedRef = useCombinedRefs(ref, focusRef);
  const treeContext = useContext(TreeContext);
  const treeNodeContext = useContext(TreeNodeContext);
  const treeBranchContext = useContext(TreeBranchContext);
  const isDisabled = treeNodeContext.disabled || treeBranchContext.disabled;

  useEffect(() => {
    if (focusItem === true) {
      combinedRef && combinedRef.current && combinedRef.current.buttonRef &&
        combinedRef.current.buttonRef.current && combinedRef.current.buttonRef.current.focus();
    }
  }, [focusItem]);

  let treeIdString = '';
  if (treeItemId !== undefined) {
    treeIdString = typeof treeItemId === 'string' ? treeItemId : treeItemId.toString();
  }
  const classes = classnames({
    [`${LIST_CONTEXT_MENU_ITEM}-${treeIdString}`]: !isDisabled && treeItemId !== undefined,
  });

  // Tab off MoreButton to next TreeItem. TreeItems use arrow keys for nav, therefore
  // this special keyboard nav to next TreeItem is used to override default Tab action.
  const handleMoreButtonKeyDown = (event) => {
    const { onKeyDown } = moreButtonProps;
    onKeyDown && onKeyDown(event);
    if (!treeContext.keyboardNavigation) {
      return;
    }
    const { key, shiftKey } = event;
    if (key !== TAB) return;
    // do not let Tab take default action
    const tabableClassName = `${LIST_ITEM_TABABLE_ITEM}-${treeContext.uniqueTreeId}`;
    // find the tree item this MoreButton belongs to, then move focus to next tree item
    const thisTreeItem = document.getElementById(`${treeItemId}`);
    // if Shift+Tab, focus on the tree item with the MoreButton
    if (shiftKey) {
      const isTreeItemNode = thisTreeItem.classList.contains(TREE_NODE);
      if (isTreeItemNode) {
        thisTreeItem.focus();
        // do not let Tab take default action
        event.preventDefault();
      } else {
        const treeBranchContent = thisTreeItem.querySelector(`.${tabableClassName}`);
        treeBranchContent && (treeBranchContent as HTMLElement).focus();
        // do not let Tab take default action
        event.preventDefault();
      }
      return;
    }
    // look for any .li children (e.g. any nodes for an opened branch)
    const nextListItemQueryChild = thisTreeItem.querySelector('li');
    // find next sibling in case there are no children
    const nextTreeItem = thisTreeItem.nextElementSibling;
    if (nextTreeItem === null) {
      const treeNodeParent = thisTreeItem.parentNode.parentNode;
      // find all .li
      const allElements = treeNodeParent.querySelectorAll('li');
      // find this item's index in all the elements
      const myIndex = _findIndex(allElements, elem => elem === thisTreeItem);
      // focus next one after this item one
      if (allElements[myIndex + 1] !== undefined) {
        allElements[myIndex + 1].focus();
        // do not let Tab take default action
        event.preventDefault();
        return;
      }
    }
    // next list item in case this is the last child of the TreeBranch
    const nextListItem = thisTreeItem.parentNode &&
      thisTreeItem.parentNode.parentNode &&
      (thisTreeItem.parentNode.parentNode as HTMLElement).nextElementSibling &&
      (thisTreeItem.parentNode.parentNode as HTMLElement).firstChild;
    if (nextListItemQueryChild !== null && nextListItemQueryChild !== undefined) {
      nextListItemQueryChild.focus();
    } else if (nextTreeItem) {
      const element = nextTreeItem.querySelector(`.${tabableClassName}`); // TreeBranchContent
      if (element === null) {
        (nextTreeItem as HTMLElement).focus();
      } else {
        nextTreeItem.firstChild && (nextTreeItem.firstChild as HTMLElement).focus(); // to Branch
      }
    } else if (nextListItem) {
      (nextListItem as HTMLElement).focus(); // to next list item TreeBrach
    }
    if (nextTreeItem !== null || nextListItemQueryChild !== null || nextListItem !== null) {
      // do not let Tab take default action
      event.preventDefault();
    }
  };

  const handleMoreButtonFocus = (event) => {
    const { onFocus } = moreButtonProps;
    onFocus && onFocus(event);
  };

  const handleMoreButtonBlur = (event) => {
    const { onBlur } = moreButtonProps;
    onBlur && onBlur(event);
  };

  const resolvedButtonProps = {
    className: classes,
    ...moreButtonProps,
  };
  return (
    <Styled.TreeMoreButton {...otherProps}>
      <MoreButton
        id={treeItemId && `${treeItemId}-treemorebutton`}
        ref={combinedRef}
        {...resolvedButtonProps}
        onKeyDown={handleMoreButtonKeyDown}
        onFocus={handleMoreButtonFocus}
        onBlur={handleMoreButtonBlur}
      >
        {children}
      </MoreButton>
    </Styled.TreeMoreButton>
  );
});

TreeMoreButton.defaultProps = {
  treeItemId: undefined,
  focusItem: false,
  moreButtonProps: {},
  children: null
};

TreeMoreButton.displayName = 'TreeMoreButton';

export {
  TreeMoreButton as default,
};
